import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ColumnFiltersState } from "@tanstack/react-table";
import { OrderAndSalesAxios } from "api/orderandsalesaxios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { FilterBuilder } from "helpers/ThunkHelper/FilterBuilder";

const API_URL = process.env.REACT_APP_BASE_URL;
const TOKEN = "a94c7ac0083ae190f0fe61143a7547799b5334c4";

const headers = {
  Authorization: `Token ${TOKEN}`,
  "Content-Type": "application/json",
};

export const fetchPaymentManagement = createAsyncThunk(
  "paymentmanagement/fetchPaymentManagement",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    type,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    type?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
  }) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", pageSize.toString());
      if (search) params.append("search", search);
      if (type === "tag") params.append("search_type", "tag");
      if (sort) {
        const orderingValue = order === "desc" ? `-${sort}` : sort;
        params.append("ordering", orderingValue);
      }

      if (filters && filters.length > 0) {
        filters.forEach(
          ({
            filterField,
            filterValue,
          }: {
            filterField: string;
            filterValue: any;
          }) => {
            if (
              filterField &&
              filterValue !== undefined &&
              filterValue !== null
            ) {
              if (typeof filterValue === "object" && filterValue !== null) {
                Object.entries(filterValue).forEach(([key, value]) => {
                  if (value !== undefined && value !== null && value !== "") {
                    params.append(key, String(value));
                  }
                  if (
                    (filterValue.from_date || filterValue.to_date) &&
                    (filterField === "created_at" ||
                      filterField === "updated_at")
                  ) {
                    if (filterField === "created_at") {
                      filterField = "created";
                    } else {
                      filterField = "updated";
                    }
                    params.append("filter_by", filterField);
                  }
                });
              } else {
                params.append(filterField, filterValue);
              }
            }
          }
        );
      }

      const response: any = await OrderAndSalesAxios.get(
        `/payment_management/all/?${params.toString()}`
      );

      return {
        data: response.data?.data || response.data || [],
        rowCount: response.data?.count || response.data?.total || 0,
      };
    } catch (error: any) {
      console.error(
        "fetchPaymentManagement error:",
        error.response?.data || error.message
      );
      throw new Error("Failed to fetch payment management");
    }
  }
);

export const fetchPaymentManagementByType = createAsyncThunk(
  "paymentmanagement/fetchPaymentManagementByType",
  async ({
    activeTab,
    page,
    pageSize,
    sort,
    order,
    search,
    type,
    filters,
  }: {
    activeTab: string;
    page: number;
    pageSize: number;
    search?: string;
    type?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
  }) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", pageSize.toString());
      if (search) params.append("search", search);
      if (type === "tag") params.append("search_type", "tag");
      if (sort) {
        const orderingValue = order === "desc" ? `-${sort}` : sort;
        params.append("ordering", orderingValue);
      }

      const typeMap: { [key: string]: string } = {
        "Order Payment": "",
        "Retention Payment": "RETENTION",
        "PBG": "PBG",
      };

      const typeValue = typeMap[activeTab] || activeTab;
      if (typeValue) {
        params.append("type", typeValue);
      }

      const filterPayload = FilterBuilder(filters ?? []);
      const body = {
        filters: filterPayload,
        ...(type === "tag" ? { search_type: "tag" } : {}),
      };

      const response: any = await OrderAndSalesAxios.post(
        `${API_ENDPOINT.PAYMENT_MANAGEMENT.PRODUCT.LIST}/?${params.toString()}`,
        body
      );

      return {
        data: response?.data?.data || response?.data,
        rowCount: response.data?.totalRecords || response.data?.count || response.data?.total || response.count || 0,
      };
    } catch (error: any) {
      console.error(
        "fetchPaymentManagementByType error:",
        error.response?.data || error.message
      );
      throw new Error("Failed to fetch payment management by type");
    }
  }
);

export const fetchServicePaymentManagementByType = createAsyncThunk(
  "paymentmanagement/fetchPaymentManagementByType",
  async ({
    activeTab,
    page,
    pageSize,
    sort,
    order,
    search,
    type,
    filters,
  }: {
    activeTab: string;
    page: number;
    pageSize: number;
    search?: string;
    type?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
  }) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", pageSize.toString());
      if (search) params.append("search", search);
      if (type === "tag") params.append("search_type", "tag");
      if (sort) {
        const orderingValue = order === "desc" ? `-${sort}` : sort;
        params.append("ordering", orderingValue);
      }

      const typeMap: { [key: string]: string } = {
        "Service Payment": "",
        "Retention Payment": "RETENTION",
        "PBG": "PBG",
      };

      const typeValue = typeMap[activeTab] || activeTab;
      if (typeValue) {
        params.append("type", typeValue);
      }

      const filterPayload = FilterBuilder(filters ?? []);
      const body = {
        filters: filterPayload,
        ...(type === "tag" ? { search_type: "tag" } : {}),
      };

      const response: any = await OrderAndSalesAxios.post(
        `${API_ENDPOINT.PAYMENT_MANAGEMENT.SERVICE.LIST}/?${params.toString()}`,
        body
      );

      return {
        data: response?.data?.data || response?.data,
        rowCount: response.data?.count || response.data?.total || response.count || 0,
      };
    } catch (error: any) {
      console.error(
        "fetchPaymentManagementByType error:",
        error.response?.data || error.message
      );
      throw new Error("Failed to fetch payment management by type");
    }
  }
);

export const fetchPaymentManagementById = createAsyncThunk(
  "paymentmanagement/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await OrderAndSalesAxios.get(
        `/payment_management/${id}/`
      );

      return response?.data?.data || response?.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch payment management by ID"
      );
    }
  }
);

export const fetchProductsAndServicesByDropDown = createAsyncThunk(
  "productsandservices/fetchByDropDown",

  async (_, { rejectWithValue }) => {
    try {
      const response = await OrderAndSalesAxios.get(
        `/products_and_services/dropdown/`
      );

      return response?.data?.data || response?.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch products and services list"
      );
    }
  }
);

// New thunk for fetching products by type for dropdowns (without pagination)
export const fetchProductsByTypeForDropdown = createAsyncThunk(
  "productsandservices/fetchProductsByTypeForDropdown",
  async (activeTab: string, { rejectWithValue }) => {
    try {
      const response = await OrderAndSalesAxios.get(
        `/products_and_services/type/${activeTab}/?page=1&page_size=1000`
      );
      return {
        data: response?.data?.data || response?.data,
        rowCount: response.data?.count || response.data?.total || 0,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch products for dropdown"
      );
    }
  }
);

export const updateProductsAndServices = createAsyncThunk(
  "productsandservices/updateProductsAndServices",
  async ({ data, id }: { data: any; id: any }, { rejectWithValue }) => {
    try {
      const response: any = await OrderAndSalesAxios.put(
        `/products_and_services/${id}/update/`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response?.data?.data || response?.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update products and services"
      );
    }
  }
);

export const updateStatusInProductsAndServices = createAsyncThunk(
  "productsandservices/updateStatusInProductsAndServices",
  async (data: any, { rejectWithValue }) => {
    try {
      const payload = { is_active: data.is_active };
      const response: any = await OrderAndSalesAxios.patch(
        `/products_and_services/${data.id}/update/`,
        payload
      );
      return response?.data?.data || response?.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update status in products and services"
      );
    }
  }
);

export const addProductsAndServices = createAsyncThunk(
  "productsandservices/addProductsAndServices",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await OrderAndSalesAxios.post(
        `/products_and_services/create/`,
        data,
        {
          headers: {
            Authorization: `Token ${TOKEN}`,
            "content-type": "multipart/form-data",
          },
        }
      );
      return response?.data?.data || response?.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to add products and services"
      );
    }
  }
);

export const deleteProductsAndServices = createAsyncThunk(
  "productsandservices/delete",
  async (id: number, { rejectWithValue }) => {
    try {
      await OrderAndSalesAxios.delete(`/products_and_services/${id}/`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const fetchCategories = createAsyncThunk(
  "productsandservices/fetchCategories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await OrderAndSalesAxios.get(
        "/products_and_services/categories/"
      );
      return response?.data?.data || response?.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch categories"
      );
    }
  }
);

export const fetchSubcategories = createAsyncThunk(
  "productsandservices/fetchSubcategories",
  async (categoryId: string, { rejectWithValue }) => {
    try {
      const response = await OrderAndSalesAxios.get(
        `/products_and_services/subcategories/${categoryId}/`
      );
      return response?.data?.data || response?.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch subcategories"
      );
    }
  }
);

export const fetchMakes = createAsyncThunk(
  "productsandservices/fetchMakes",
  async (
    {
      categoryId,
      subcategoryId,
    }: { categoryId: string; subcategoryId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await OrderAndSalesAxios.get(
        `/products_and_services/makes/${categoryId}/${subcategoryId}/`
      );
      return response?.data?.data || response?.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to fetch makes");
    }
  }
);

export const fetchModels = createAsyncThunk(
  "productsandservices/fetchModels",
  async (
    {
      categoryId,
      subcategoryId,
      makeId,
    }: { categoryId: string; subcategoryId: string; makeId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await OrderAndSalesAxios.get(
        `/products_and_services/models/${categoryId}/${subcategoryId}/${makeId}/`
      );
      return response?.data?.data || response?.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to fetch models");
    }
  }
);

// Payment Management CRUD operations
export const createPaymentManagement = createAsyncThunk(
  "paymentmanagement/createPaymentManagement",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await OrderAndSalesAxios.post(
        `/payment_management/create/`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response?.data?.data || response?.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to create payment management"
      );
    }
  }
);

export const updatePaymentManagement = createAsyncThunk(
  "paymentmanagement/updatePaymentManagement",
  async ({ data, id }: { data: any; id: string }, { rejectWithValue }) => {
    try {
      const response: any = await OrderAndSalesAxios.put(
        `/payment_management/${id}/update/`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response?.data?.data || response?.data?.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update payment management"
      );
    }
  }
);

export const deletePaymentManagement = createAsyncThunk(
  "paymentmanagement/deletePaymentManagement",
  async (id: string, { rejectWithValue }) => {
    try {
      await OrderAndSalesAxios.delete(`/payment_management/${id}/delete/`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Payment Follow-up operations
export const fetchPaymentFollowUps = createAsyncThunk(
  "paymentmanagement/fetchPaymentFollowUps",
  async (paymentId: string, { rejectWithValue }) => {
    try {
      const response = await OrderAndSalesAxios.get(
        `/payment_management/${paymentId}/followups/`
      );
      return response?.data?.data || response?.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch payment follow-ups"
      );
    }
  }
);

export const createPaymentFollowUp = createAsyncThunk(
  "paymentmanagement/createPaymentFollowUp",
  async (
    { paymentId, data }: { paymentId: string; data: any },
    { rejectWithValue }
  ) => {
    try {
      const response = await OrderAndSalesAxios.post(
        `/payment_management/${paymentId}/followups/create/`,
        data
      );
      return response?.data?.data || response?.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to create payment follow-up"
      );
    }
  }
);

export const updatePaymentFollowUp = createAsyncThunk(
  "paymentmanagement/updatePaymentFollowUp",
  async (
    {
      paymentId,
      followUpId,
      data,
    }: { paymentId: string; followUpId: string; data: any },
    { rejectWithValue }
  ) => {
    try {
      const response = await OrderAndSalesAxios.put(
        `/payment_management/${paymentId}/followups/${followUpId}/update/`,
        data
      );
      return response?.data?.data || response?.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to update payment follow-up"
      );
    }
  }
);

export const deletePaymentFollowUp = createAsyncThunk(
  "paymentmanagement/deletePaymentFollowUp",
  async (
    { paymentId, followUpId }: { paymentId: string; followUpId: string },
    { rejectWithValue }
  ) => {
    try {
      await OrderAndSalesAxios.delete(
        `/payment_management/${paymentId}/followups/${followUpId}/delete/`
      );
      return followUpId;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to delete payment follow-up"
      );
    }
  }
);

export const exportPaymentManagementCSV = async ({
  activeTab,
  search,
  filters,
  sort,
  order,
}: {
  activeTab?: string;
  search?: string;
  filters?: any;
  sort?: string;
  order?: string;
}) => {
  const params = new URLSearchParams();

  const typeMap: { [key: string]: string } = {
    "Order Payment": "",
    "Retention Payment": "RETENTION",
    "PBG": "PBG",
  };

  const typeValue = activeTab ? (typeMap[activeTab] || activeTab) : "";
  if (typeValue) {
    params.append("type", typeValue);
  }

  if (search) params.append("search", search);
  
  if (sort) params.append("sort", sort);
  if (order) params.append("order", order);

  if (filters && filters.length > 0) {
    filters.forEach(
      ({
        filterField,
        filterValue,
      }: {
        filterField: string;
        filterValue: any;
      }) => {
        if (filterField && filterValue !== undefined && filterValue !== null) {
          if (typeof filterValue === "object" && filterValue !== null) {
            Object.entries(filterValue).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== "") {
                params.append(key, String(value));
              }
              if (
                (filterValue.from_date || filterValue.to_date) &&
                (filterField === "created_at" || filterField === "updated_at")
              ) {
                if (filterField === "created_at") {
                  filterField = "created";
                } else {
                  filterField = "updated";
                }
                params.append("filter_by", filterField);
              }
            });
          } else {
            params.append(filterField, filterValue);
          }
        }
      }
    );
  }

  const filterPayload = FilterBuilder(filters ?? []);
  const body = {
    filters: filterPayload,
  };

  const response: any = await OrderAndSalesAxios.post(
    `${API_ENDPOINT.PAYMENT_MANAGEMENT.PRODUCT.EXPORT}?${params.toString()}`,
    body,
    {
      responseType: "blob",
    }
  );

  const defaultFilename = activeTab ? `Payment_management_${activeTab.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.csv` : "payment_management.csv";

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || defaultFilename,
  };
};

export const exportServicePaymentManagementCSV = async ({
  activeTab,
  search,
  filters,
  sort,
  order,
}: {
  activeTab?: string;
  search?: string;
  filters?: any;
  sort?: string;
  order?: string;
}) => {
  const params = new URLSearchParams();

  const typeMap: { [key: string]: string } = {
    "Service Payment": "",
    "Retention Payment": "RETENTION",
    "PBG": "PBG",
  };

  const typeValue = activeTab ? (typeMap[activeTab] || activeTab) : "";
  if (typeValue) {
    params.append("type", typeValue);
  }

  if (search) params.append("search", search);
  
  if (sort) params.append("sort", sort);
  if (order) params.append("order", order);

  if (filters && filters.length > 0) {
    filters.forEach(
      ({
        filterField,
        filterValue,
      }: {
        filterField: string;
        filterValue: any;
      }) => {
        if (filterField && filterValue !== undefined && filterValue !== null) {
          if (typeof filterValue === "object" && filterValue !== null) {
            Object.entries(filterValue).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== "") {
                params.append(key, String(value));
              }
              if (
                (filterValue.from_date || filterValue.to_date) &&
                (filterField === "created_at" || filterField === "updated_at")
              ) {
                if (filterField === "created_at") {
                  filterField = "created";
                } else {
                  filterField = "updated";
                }
                params.append("filter_by", filterField);
              }
            });
          } else {
            params.append(filterField, filterValue);
          }
        }
      }
    );
  }

  const filterPayload = FilterBuilder(filters ?? []);
  const body = {
    filters: filterPayload,
  };

  const response: any = await OrderAndSalesAxios.post(
    `${API_ENDPOINT.PAYMENT_MANAGEMENT.SERVICE.EXPORT}?${params.toString()}`,
    body,
    {
      responseType: "blob",
    }
  );

  const defaultFilename = activeTab ? `Payment_management_${activeTab.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.csv` : "payment_management.csv";

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || defaultFilename,
  };
};

export const fetchPaymentHistory = createAsyncThunk(
  "paymentmanagement/fetchPaymentHistory",
  async ({ orderId, orderType, paymentType }: { orderId: string, orderType: string, paymentType?: string }, { rejectWithValue }) => {
    try {
      const url = API_ENDPOINT.PAYMENT_MANAGEMENT.RECORD_PAYMENT.HISTORY(orderId, orderType, paymentType);
      console.log("fetchPaymentHistory URL:", url);
      const response: any = await OrderAndSalesAxios.get(url);
      const data = response?.data?.data || response?.data || {};

      // Map backend fields to frontend format
      // Convert UTC dates to local for display
      const convertUTCToLocalDate = (utcDateStr: string): string => {
        if (!utcDateStr) return "";
        try {
          const utcDate = new Date(utcDateStr);
          if (isNaN(utcDate.getTime())) return "";
          return utcDate.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          }).replace(/ /g, '-');
        } catch (error) {
          console.error("Error converting UTC to local date:", error);
          return "";
        }
      };
      
      const payments = (data.payments || []).map((p: any) => ({
        id: p.record_payment_id,
        date: p.payment_date ? convertUTCToLocalDate(p.payment_date) : "-",
        totalAmount: p.total_order_amount || data.grand_total_amount || 0,
        receivedAmount: Number(p.received_amount) || 0,
        // Clamp pending amount to 0 (never allow negative values)
        pendingAmount: Math.max(0, p.amount_pending !== null && p.amount_pending !== undefined ? Number(p.amount_pending) : 0),
        modeOfPayment: p.mode_of_payment,
        transactionRef: p.transaction_ref_no,
        remark: p.remark,
        created_by_user: p.created_by_user
      }));

      return payments;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch payment history"
      );
    }
  }
);

export const addRecordPayment = createAsyncThunk(
  "paymentmanagement/addRecordPayment",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await OrderAndSalesAxios.post(
        API_ENDPOINT.PAYMENT_MANAGEMENT.RECORD_PAYMENT.CREATE,
        data
      );
      return response?.data?.data || response?.data;
    } catch (err: any) {
      // Return specific error message if available
      return rejectWithValue(
        err.response?.data?.message || err.response?.data || "Failed to add record payment"
      );
    }
  }
);

export const removeRecordPayment = createAsyncThunk(
  "paymentmanagement/removeRecordPayment",
  async (paymentId: string, { rejectWithValue }) => {
    try {
      await OrderAndSalesAxios.delete(
        API_ENDPOINT.PAYMENT_MANAGEMENT.RECORD_PAYMENT.DELETE(paymentId)
      );
      return paymentId;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to delete record payment"
      );
    }
  }
);

export const addFollowUp = createAsyncThunk(
  "paymentmanagement/addFollowUp",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await OrderAndSalesAxios.post(
        API_ENDPOINT.PAYMENT_MANAGEMENT.FOLLOW_UP.CREATE,
        data
      );
      return response?.data?.data || response?.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to create follow-up");
    }
  }
);
